/** Example code for integration clients (Super Admin → Integrations → Integration guide). */

export type IntegrationSnippetLanguage = "curl" | "node" | "dotnet" | "page";

export const INTEGRATION_SECRET_HEADER = "X-ConnectAI-Secret";
const SECRET_PLACEHOLDER = "cai_sk_…";

/** Absolute API base (e.g. https://connect.example.com/report/v1) for calls from the client's backend. */
export function integrationApiBase(basePath: string, origin: string): string {
  try {
    return new URL(basePath || "/", origin).toString().replace(/\/+$/, "");
  } catch {
    return `${origin}${basePath || ""}`.replace(/\/+$/, "");
  }
}

export function buildIntegrationSnippet(language: IntegrationSnippetLanguage, apiBase: string): string {
  switch (language) {
    case "curl":
      return [
        "# 1) Create the user (once, when the account is created in your system)",
        `curl -X POST "${apiBase}/integration/v1/users" \\`,
        `  -H "${INTEGRATION_SECRET_HEADER}: ${SECRET_PLACEHOLDER}" \\`,
        '  -H "Content-Type: application/json" \\',
        `  -d '{"email":"ayse.yilmaz@example.com","password":"S3cure-Passw0rd!","firstName":"Ayşe","lastName":"Yılmaz","externalUserId":"ERP-10042"}'`,
        "",
        "# 2) For every page view: a one-time login ticket (valid ~60 s)",
        `curl -X POST "${apiBase}/integration/v1/login-tickets" \\`,
        `  -H "${INTEGRATION_SECRET_HEADER}: ${SECRET_PLACEHOLDER}" \\`,
        '  -H "Content-Type: application/json" \\',
        `  -d '{"externalUserId":"ERP-10042"}'`,
      ].join("\n");
    case "node":
      return [
        "// Server side only — never expose the secret to the browser.",
        `const API = "${apiBase}/integration/v1";`,
        "const headers = {",
        `  "${INTEGRATION_SECRET_HEADER}": process.env.CONNECT_AI_SECRET,`,
        '  "Content-Type": "application/json",',
        "};",
        "",
        "export async function createConnectAiUser(user) {",
        "  const res = await fetch(`${API}/users`, {",
        '    method: "POST",',
        "    headers,",
        "    body: JSON.stringify({",
        "      email: user.email,",
        "      password: user.initialPassword,",
        "      firstName: user.firstName,",
        "      lastName: user.lastName,",
        "      externalUserId: String(user.id),",
        "    }),",
        "  });",
        "  if (res.status !== 201 && res.status !== 200) throw new Error(await res.text());",
        "  return res.json();",
        "}",
        "",
        "export async function loginTicket(userId) {",
        "  const res = await fetch(`${API}/login-tickets`, {",
        '    method: "POST",',
        "    headers,",
        "    body: JSON.stringify({ externalUserId: String(userId) }),",
        "  });",
        "  if (!res.ok) throw new Error(await res.text());",
        "  return (await res.json()).ticket;",
        "}",
      ].join("\n");
    case "dotnet":
      return [
        "// Server side only — never expose the secret to the browser.",
        "using System.Net.Http.Json;",
        "",
        "var http = new HttpClient { BaseAddress = new Uri(\"" + apiBase + "/integration/v1/\") };",
        `http.DefaultRequestHeaders.Add("${INTEGRATION_SECRET_HEADER}", Environment.GetEnvironmentVariable("CONNECT_AI_SECRET"));`,
        "",
        "// Create the user",
        "var created = await http.PostAsJsonAsync(\"users\", new {",
        "    email = \"ayse.yilmaz@example.com\",",
        "    password = \"S3cure-Passw0rd!\",",
        "    firstName = \"Ayşe\",",
        "    lastName = \"Yılmaz\",",
        "    externalUserId = \"ERP-10042\"",
        "});",
        "created.EnsureSuccessStatusCode();",
        "",
        "// One-time login ticket for the current page view",
        "var res = await http.PostAsJsonAsync(\"login-tickets\", new { externalUserId = \"ERP-10042\" });",
        "res.EnsureSuccessStatusCode();",
        "var ticket = (await res.Content.ReadFromJsonAsync<TicketResponse>())!.Ticket;",
        "",
        "record TicketResponse(string Ticket, DateTime ExpiresAt, string EmbedKey);",
      ].join("\n");
    case "page":
      return [
        "<!-- After the embed snippet: sign the current user in without a password. -->",
        "<script>",
        "  // The ticket comes from your backend (POST /integration/v1/login-tickets), rendered into the page.",
        '  GARChat("login", { ticket: "{{ connectAiTicket }}" });',
        "  // Optional: fetch a fresh ticket when sign-in fails (e.g. the page stayed open too long).",
        '  GARChat("on", "ticket", function (result) { if (!result.ok) { /* request a new ticket and call login again */ } });',
        "</script>",
      ].join("\n");
  }
}
